/**
 * Presentation resolution (D132) — the pack's optional `presentation` block
 * filled from the built-in table, so a shell reads one complete shape and
 * never asks "and if the pack said nothing?".
 *
 * @remarks
 * The D25 shape, applied to chrome: a general default, a pack override, one
 * code path. The defaults are deliberately plain — a wake gate labeled WAKE,
 * a begin button labeled BEGIN, status lines that say nothing about
 * terminals — because they are what a pack gets for declaring nothing, and
 * nothing a pack did not write should sound like fiction.
 *
 * Three slots resolve to ABSENCE rather than a default, and absence is the
 * honest reading each time: no `logo` means the game's title set in type
 * (Mike's ruling: the name on the door is the game's, never the engine's);
 * no `hero` means a title screen with no picture; no `theme` means a silent
 * title. A default picture or a default tune would be the engine's face on a
 * stranger's game.
 *
 * The theme resolves here to its `AudioDefinition` rather than its id, so the
 * shell that plays it never looks anything up. A theme naming no declared
 * asset resolves to NO theme — this function is a pure read and refuses
 * nothing; refusing the pack is `validateAudioAuthoring`'s job, and it runs
 * before any bundle assembles. Stating the arm here keeps it a tested branch
 * rather than a throw for a state the loader already rules out.
 *
 * @packageDocumentation
 */

import type { AudioDefinition, PackManifest, Surface, TitleScreenPresentation } from '@orpheus/content-schema';

import { DEFAULT_MAP_STYLE, type ResolvedMapPresentation, resolveMapPresentation } from '@orpheus/engine-runtime';

/** The title screen, fully resolved — every label present, the assets present or honestly absent. */
export interface ResolvedTitleScreen {
	/** The second-step button's label. */
	readonly beginLabel: string;
	/** The full-bleed picture's pack-relative path, when the pack declares one. */
	readonly hero?: string;
	/** D179: the start-over door's confirm — the sentence (`{count}` is the locations walked) and its two buttons. */
	readonly restart: { readonly confirm: string; readonly keepLabel: string; readonly proceedLabel: string };
	/** D179: the start-over door's label. */
	readonly restartLabel: string;
	/** D179: the second-step button's label when a descent is under way. */
	readonly resumeLabel: string;
	/** The typed status lines: before the wake, after it, and (D179) when a descent is under way. */
	readonly status: { readonly asleep: string; readonly awake: string; readonly inProgress: string };
	/** The theme that loops under the title, when the pack declares one. */
	readonly theme?: AudioDefinition;
	/** The first-step button's label — the audio gesture. */
	readonly wakeLabel: string;
}

/** The pack's presentation, fully resolved for a shell. */
export interface ResolvedPresentation {
	/** The game's own lockup's pack-relative path, when the pack declares one. */
	readonly logo?: string;
	/** How the map is drawn (D169). */
	readonly map: ResolvedMapPresentation;
	/** The masthead's second line, when the pack declares one. */
	readonly subtitle?: string;
	readonly titleScreen: ResolvedTitleScreen;
	/** The face the world speaks in (D203); `terminal` unless the pack says `chart`. */
	readonly surface: Surface;
}

// The map half resolves in the engine (D189: the `MAP` render reads it too),
// re-exported here so a shell keeps reading one presentation module.
export { DEFAULT_MAP_STYLE, type ResolvedMapPresentation };

/** What a pack gets for saying nothing: plain, fiction-free labels. */
export const DEFAULT_TITLE_SCREEN_LABELS = {
	beginLabel: 'BEGIN',
	// D179: the same sentence the engine's `restartConfirm` slot speaks in
	// play, because it is the same act; a pack re-cuts both or neither.
	restart: { confirm: 'This throws away everything you have walked. There is no copy.', keepLabel: 'KEEP IT', proceedLabel: 'START OVER' },
	restartLabel: 'BEGIN AGAIN',
	resumeLabel: 'RESUME',
	// D183: a LABELED readout, not a counted noun — "LOCATIONS WALKED: 1"
	// cannot disagree with its own number the way "1 LOCATIONS WALKED" does,
	// and it matches the register the inventory readout already uses.
	status: { asleep: 'STANDING BY', awake: 'READY', inProgress: 'IN PROGRESS — LOCATIONS WALKED: {count}' },
	wakeLabel: 'WAKE',
} satisfies Pick<ResolvedTitleScreen, 'beginLabel' | 'restart' | 'restartLabel' | 'resumeLabel' | 'status' | 'wakeLabel'>;

/**
 * Resolve a manifest's presentation block against the built-in defaults.
 *
 * @param manifest - The parsed pack manifest
 * @param audio - The pack's audio table, for the theme lookup
 * @returns The complete presentation a shell renders from
 */
export const resolvePresentation = (
	manifest: PackManifest,
	audio: readonly AudioDefinition[],
): ResolvedPresentation => {
	const declared = manifest.presentation;

	return {
		...(declared?.logo === undefined ? {} : { logo: declared.logo }),
		map: resolveMapPresentation(declared?.map),
		...(declared?.subtitle === undefined ? {} : { subtitle: declared.subtitle }),
		// D203: absence is the terminal, so a pack that says nothing is dressed exactly as before the island.
		surface: declared?.surface ?? 'terminal',
		titleScreen: resolveTitleScreen(declared?.titleScreen, audio),
	};
};

/**
 * Resolve the title-screen half: labels from the table, assets as declared.
 *
 * @param declared - The pack's title-screen block, if any
 * @param audio - The pack's audio table, for the theme lookup
 * @returns The complete title screen
 */
/**
 * A flat record of labels, each the declared word or the built-in one.
 *
 * @param declared - What the pack said, possibly nothing
 * @param defaults - The built-in table for this record
 * @returns Every key filled
 */
const withDefaults = <Shape extends Record<string, string>>(declared: Partial<Record<keyof Shape, string>> | undefined, defaults: Shape): Shape =>
	Object.fromEntries(Object.entries(defaults).map(([key, fallback]) => [key, declared?.[key] ?? fallback])) as Shape;

const resolveTitleScreen = (
	declared: TitleScreenPresentation | undefined,
	audio: readonly AudioDefinition[],
): ResolvedTitleScreen => {
	const theme = declared?.theme === undefined ? undefined : audio.find((definition) => definition.audioId === declared.theme);
	const { restart: restartDefaults, status: statusDefaults, ...labelDefaults } = DEFAULT_TITLE_SCREEN_LABELS;

	return {
		...withDefaults(declared, labelDefaults),
		...(declared?.hero === undefined ? {} : { hero: declared.hero }),
		restart: withDefaults(declared?.restart, restartDefaults),
		status: withDefaults(declared?.status, statusDefaults),
		...(theme === undefined ? {} : { theme }),
	};
};
